import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, ArrowRight } from "lucide-react";
import CuratorBadge from "./CuratorBadge";

const QUESTIONS = [
  {
    q: "Pick a print:",
    options: [
      { label: "Loud floral 🌺", who: "boggy" },
      { label: "Solid color, classic", who: "fox" },
    ],
  },
  {
    q: "Saturday afternoon:",
    options: [
      { label: "Pool party 🩳", who: "boggy" },
      { label: "Coffee shop reading ☕", who: "fox" },
    ],
  },
  {
    q: "Color philosophy:",
    options: [
      { label: "Louder is better", who: "boggy" },
      { label: "Less is more", who: "fox" },
    ],
  },
  {
    q: "Pick your closet hero:",
    options: [
      { label: "1986 Hawaiian shirt", who: "boggy" },
      { label: "Vintage 501 Levi's", who: "fox" },
    ],
  },
];

const RESULTS = {
  boggy: {
    name: "Boggy",
    species: "the frog",
    accent: "bg-green",
    accentText: "text-ink",
    headline: "You're a 🐸 Boggy",
    blurb: "Loud, bright, and unbothered. You wear color like it owes you money. Boggy approves.",
    code: "BOGGYSTYLE10",
    cta: "Shop Boggy's picks",
    href: "/boggy",
  },
  fox: {
    name: "Fox",
    species: "the fox",
    accent: "bg-orange",
    accentText: "text-white",
    headline: "You're a 🦊 Fox",
    blurb: "Clean, considered, and perfectly cut. You'd rather own one great piece than ten okay ones. Fox respects.",
    code: "FOXSTYLE10",
    cta: "Shop Fox's picks",
    href: "/fox",
  },
};

export default function StyleQuiz({ open, onClose }) {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({ boggy: 0, fox: 0 });
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const result = scores.boggy >= scores.fox ? RESULTS.boggy : RESULTS.fox;

  const pick = (who) => {
    const next = { ...scores, [who]: scores[who] + 1 };
    setScores(next);
    if (step + 1 >= QUESTIONS.length) setDone(true);
    else setStep(step + 1);
  };

  const reset = () => { setStep(0); setScores({ boggy: 0, fox: 0 }); setDone(false); setCopied(false); };

  const close = () => { reset(); onClose(); };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#1A1A1A]/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="style-quiz">
      <div className="brut bg-paper w-full max-w-lg my-8 relative">
        <button onClick={close} className="absolute -top-4 -right-4 brut-btn bg-white p-2" data-testid="quiz-close" aria-label="close">
          <X size={18} strokeWidth={3} />
        </button>

        {!done ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="tag-sticker bg-yellow text-lg rotate-tilt-l flex items-center gap-1 w-fit">
                <Sparkles size={14} strokeWidth={3} /> what's your style?
              </span>
              <span className="font-bold uppercase tracking-wider text-xs opacity-70" data-testid="quiz-progress">
                {step + 1} / {QUESTIONS.length}
              </span>
            </div>
            <h2 className="font-display text-3xl uppercase mt-2">{QUESTIONS[step].q}</h2>
            <div className="mt-5 grid grid-cols-1 gap-3" data-testid={`quiz-q-${step}`}>
              {QUESTIONS[step].options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => pick(opt.who)}
                  className="brut-btn bg-white px-5 py-4 text-left font-bold text-base hover:bg-cyan transition-colors"
                  data-testid={`quiz-option-${step}-${i}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="mt-4 h-2 bg-white border-[3px] border-[#1A1A1A] overflow-hidden">
              <div className="h-full bg-orange transition-all" style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
            </div>
          </div>
        ) : (
          <div className={`p-6 ${result.accent} ${result.accentText}`} data-testid={`quiz-result-${result.name.toLowerCase()}`}>
            <div className="flex items-center gap-3">
              <CuratorBadge curator={result.name.toLowerCase()} size="lg" />
              <span className="tag-sticker bg-paper text-ink text-lg rotate-tilt-l">drumroll…</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase mt-3 leading-tight">{result.headline}</h2>
            <p className="mt-3 text-base sm:text-lg">{result.blurb}</p>

            <div className="mt-5 brut p-4 bg-paper text-ink">
              <div className="text-xs font-bold uppercase tracking-wider opacity-70">your code · 10% off $25+</div>
              <div className="flex items-center justify-between mt-2 gap-3">
                <span className="font-display text-2xl sm:text-3xl bg-yellow border-[3px] border-[#1A1A1A] px-3 py-1" data-testid="quiz-code">{result.code}</span>
                <button onClick={copyCode} className="brut-btn bg-white px-3 py-2 text-sm" data-testid="quiz-copy">
                  {copied ? "copied!" : "copy"}
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => { close(); nav(result.href); }}
                className="brut-btn bg-paper text-ink px-5 py-3 flex items-center gap-2"
                data-testid="quiz-go-shop"
              >
                {result.cta} <ArrowRight size={18} strokeWidth={3} />
              </button>
              <button
                onClick={() => { close(); nav(`/quiz/${result.name.toLowerCase()}`); }}
                className="brut-btn bg-paper text-ink px-4 py-3 text-sm"
                data-testid="quiz-share-result"
              >
                Share my result
              </button>
              <button onClick={reset} className="brut-btn bg-paper text-ink px-4 py-3 text-sm" data-testid="quiz-retake">
                Retake
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
